/**
 * MESSAGE MAPPERS — boundary UI <-> DTO REST.
 *
 * Shape backend (rest-openapi.yaml, MessagesResourceImpl):
 *   Message { messageId, audit, subject, body, senderName,
 *             senderEmailAddress, receiptDate, label: int32,
 *             infos: MessageInfo[] (predefinite, read-only),
 *             extractorInfos: ExtractorGroup[] (info extractor raggruppate per
 *               provenienza, editabili) }
 *   MessageInput { subject, body, senderName, senderEmailAddress,
 *                  receiptDate?, label?: int32 }
 *
 * `Message.label` server-side e' un int32 (enum MessageLabel: 0=NEW,
 * 1=TO_PROCESS, 2=IN_PROGRESS, 3=COMPLETED, 4=IGNORED). Nel dominio UI
 * lo esponiamo come union string (vedi messageTypes.MessageLabel).
 *
 * SYNC CROSS-LAYER — se il backend aggiunge/toglie/rinomina valori
 * dell'enum MessageLabel, aggiornare IN PARALLELO:
 *   - `messageMappers.ts` (questo file, tabella MESSAGE_LABEL_TO_INT)
 *   - `messageTypes.ts` (type union MessageLabel + array MESSAGE_LABELS con label i18n + color MUI)
 *   - `locales/{it,en}.json` (chiavi message.labels.*)
 *   - Java: `modules/crm-util/src/main/java/it/dvel/crm/util/constants/MessageLabel.java`
 */

import { pickAuditFields, type AuditFields } from '@crm/shared';
import type { Message, MessageAttachment, MessageDirection, MessageLabel } from './messageTypes';
import {
  extractorGroupFromDto,
  extractorGroupToDto,
  messageInfoFromDto,
  messageInfoToDto,
  type ExtractorGroupDto,
  type MessageInfoDto,
} from './messageInfoMappers';

// ─── MessageLabel ──────────────────────────────────────────────────────
// SYNC with MessageLabel.java: NEW=0, TO_PROCESS=1, IN_PROGRESS=2,
// COMPLETED=3, IGNORED=4, ERROR=10
// File: modules/crm-util/src/main/java/it/dvel/crm/util/constants/MessageLabel.java
const MESSAGE_LABEL_TO_INT: Record<MessageLabel, number> = {
  new: 0,
  to_process: 1,
  in_progress: 2,
  completed: 3,
  ignored: 4,
  error: 10,
};

const MESSAGE_LABEL_FROM_INT: Record<number, MessageLabel> = {
  0: 'new',
  1: 'to_process',
  2: 'in_progress',
  3: 'completed',
  4: 'ignored',
  10: 'error',
};

/** int -> MessageLabel con fallback 'new' e warn per qualsiasi valore
 *  inatteso (incluso null/undefined: il backend dovrebbe sempre inviare
 *  un int; un null indica solitamente un bug lato server o un DTO
 *  parzialmente inizializzato). */
function messageLabelFromInt(value: number | null | undefined): MessageLabel {
  if (value == null) {
    console.warn(`[messageMappers] Message.label missing from DTO (null/undefined), falling back to 'new'`);
    return 'new';
  }
  const mapped = MESSAGE_LABEL_FROM_INT[value];
  if (mapped !== undefined) return mapped;
  console.warn(`[messageMappers] Unexpected Message.label value from DTO: ${value}, falling back to 'new'`);
  return 'new';
}

export function messageLabelToInt(label: MessageLabel): number {
  return MESSAGE_LABEL_TO_INT[label];
}

export interface MessageAttachmentDto {
  messageAttachmentId: number;
  messageId: number;
  fileName: string;
  extension: string;
  mimeType: string;
  size: number;
}

/** DTO -> modello UI per un allegato (shape identica, mapping esplicito per coerenza). */
function messageAttachmentFromDto(dto: MessageAttachmentDto): MessageAttachment {
  return {
    messageAttachmentId: dto.messageAttachmentId,
    messageId: dto.messageId,
    fileName: dto.fileName,
    extension: dto.extension,
    mimeType: dto.mimeType,
    size: dto.size,
  };
}

/** Modello UI -> DTO per un allegato. Usato dai mock per simulare la shape backend. */
function messageAttachmentToDto(a: MessageAttachment): MessageAttachmentDto {
  return {
    messageAttachmentId: a.messageAttachmentId,
    messageId: a.messageId,
    fileName: a.fileName,
    extension: a.extension,
    mimeType: a.mimeType,
    size: a.size,
  };
}

export interface MessageDto {
  messageId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  direction?: string;
  externalId?: string;
  parentExternalId?: string | null;
  rootExternalId?: string;
  subject: string;
  body: string;
  bodyHtml?: string | null;
  senderName: string;
  senderEmailAddress: string;
  receiptDate?: string | null;
  label?: number | null;
  summary?: string;
  infos?: MessageInfoDto[] | null;
  extractorInfos?: ExtractorGroupDto[] | null;
  attachments?: MessageAttachmentDto[] | null;
}

export interface MessageInput {
  subject?: string;
  body?: string;
  senderName?: string;
  senderEmailAddress?: string;
  receiptDate?: string | null;
  label?: number | null;
}

/** DTO -> modello UI. Convert label int -> MessageLabel, mappa infos[]. */
export function messageFromDto(dto: MessageDto): Message {
  return {
    messageId: dto.messageId,
    ...pickAuditFields(dto),
    direction: dto.direction as MessageDirection,
    externalId: dto.externalId,
    parentExternalId: dto.parentExternalId ?? undefined,
    rootExternalId: dto.rootExternalId,
    subject: dto.subject,
    body: dto.body,
    bodyHtml: dto.bodyHtml ?? undefined,
    senderName: dto.senderName,
    senderEmailAddress: dto.senderEmailAddress,
    receiptDate: dto.receiptDate ?? '',
    label: messageLabelFromInt(dto.label),
    summary: dto.summary,
    infos: (dto.infos ?? []).map(messageInfoFromDto),
    extractorInfos: (dto.extractorInfos ?? []).map(extractorGroupFromDto),
    attachments: (dto.attachments ?? []).map(messageAttachmentFromDto),
  };
}

/** Modello UI -> DTO REST. Usato dai mock per simulare la shape backend. */
export function messageToDto(m: Message): MessageDto {
  const base: Message & AuditFields = m;
  return {
    messageId: base.messageId,
    ...pickAuditFields(base),
    direction: base.direction,
    externalId: base.externalId,
    parentExternalId: base.parentExternalId,
    rootExternalId: base.rootExternalId,
    subject: base.subject,
    body: base.body,
    bodyHtml: base.bodyHtml,
    senderName: base.senderName,
    senderEmailAddress: base.senderEmailAddress,
    receiptDate: base.receiptDate,
    label: messageLabelToInt(base.label),
    summary: base.summary,
    infos: base.infos.map(messageInfoToDto),
    extractorInfos: (base.extractorInfos ?? []).map(extractorGroupToDto),
    attachments: (base.attachments ?? []).map(messageAttachmentToDto),
  };
}

/** Payload create UI -> DTO input. */
export function messageCreateToInput(p: {
  subject: string;
  body: string;
  senderName: string;
  senderEmailAddress: string;
  receiptDate?: string | null;
  label?: MessageLabel | null;
}): MessageInput {
  return {
    subject: p.subject,
    body: p.body,
    senderName: p.senderName,
    senderEmailAddress: p.senderEmailAddress,
    receiptDate: p.receiptDate ?? null,
    label: p.label != null ? messageLabelToInt(p.label) : null,
  };
}

/**
 * Payload update label UI -> DTO input (PATCH /message/{id}/label). Il contenuto della mail e'
 * immutabile: l'unico campo aggiornabile e' il label (stato di lavorazione).
 */
export function messageLabelToInput(label: MessageLabel): MessageInput {
  return { label: messageLabelToInt(label) };
}

/** DTO input (PATCH body) -> Partial<Message> UI. Usato dai mock. */
export function messageInputFromDto(input: MessageInput): Partial<Message> {
  const out: Partial<Message> = {};
  if (input.subject !== undefined) out.subject = input.subject;
  if (input.body !== undefined) out.body = input.body;
  if (input.senderName !== undefined) out.senderName = input.senderName;
  if (input.senderEmailAddress !== undefined) out.senderEmailAddress = input.senderEmailAddress;
  if (input.receiptDate !== undefined) out.receiptDate = input.receiptDate ?? '';
  if (input.label !== undefined && input.label !== null) out.label = messageLabelFromInt(input.label);
  return out;
}

// Re-export dei type MessageDirection/MessageLabel per comodita'.
export type { MessageDirection, MessageLabel };
